import { Box } from "@/components/ui/box";
import { Button, ButtonIcon, ButtonSpinner, ButtonText } from "@/components/ui/button";
import { HStack } from "@/components/ui/hstack";
import { tva } from "@gluestack-ui/nativewind-utils/tva";
import { T } from "@transifex/react";
import { type FC, useCallback } from "react";

const buttonStyle = tva({
  base: "h-[52px] rounded-md *:font-semibold shadow-[0px_4px_10px_0px_#0000001A] flex items-center justify-center",
  variants: {
    variant: {
      primary:
        "bg-primary-900 border-[1px] border-primary-800  *:text-typography-300 data-[hover=true]:bg-primary-700 data-[active=true]:bg-primary-500 data-[active=true]:outline data-[active=true]:outline-primary-700 data-[active=true]:outline-1",
      outline:
        "bg-background-100 border-[1px] border-outline-300 *:text-typography-900  data-[hover=true]:bg-outline-300 data-[hover=true]:border-0 data-[active=true]:border-[1px] data-[active=true]:border-outline-300 data-[active=true]:bg-outline-300",
    },
    size: {
      sm: "py-0 px-2 text-sm",
      md: "py-4 px-4 text-base",
      lg: "py-0 px-2 text-lg",
    },
    disabled: {
      true: "",
    },
  },
  compoundVariants: [
    {
      variant: "primary",
      disabled: true,
      className: "bg-secondary-200 border-secondary-800 *:text-typography-800",
    },
    {
      variant: "outline",
      disabled: true,
      className: "bg-background-100 border-secondary-800 *:text-typography-800",
    },
  ],
});

type Variant = "primary" | "outline";
export interface AppButtonProps {
  onPress: () => void;
  title: string;
  icon?: FC;
  iconPosition?: "before" | "after";
  children?: React.ReactElement | React.ReactElement[];
  loading?: boolean;
  variant?: Variant;
  disabled?: boolean;
  className?: string;
  textClassName?: string;
  testID?: string;
}

const AppButton = ({
  onPress,
  title,
  icon,
  loading = false,
  iconPosition = "before",
  children,
  variant = "primary",
  disabled = false,
  className,
  testID,
  textClassName,
}: AppButtonProps) => {
  const handlePressButton = useCallback(() => {
    if (!loading) {
      onPress();
    }
  }, [loading, onPress]);

  const renderButtonContent = () => {
    if (children) return <>{children}</>;

    if (loading) return <ButtonSpinner color={"#000"} />;

    const color = disabled ? "text-typography-800" : variant === "outline" ? "text-typography-900" : "text-typography-300";

    const iconElement =
      typeof icon === "function" ? icon({ strokeWidth: 2, size: "md", className: color, fill: color }) : icon && <ButtonIcon size={"md"} as={icon} />;

    return (
      <Box className="flex items-center justify-center">
        <HStack className={`flex items-center gap-x-[8] ${iconPosition === "after" ? "flex-row-reverse" : ""}`}>
          {iconElement && <Box>{iconElement}</Box>}
          <Box>
            <ButtonText className={`${color} leading-[22px] ${textClassName}`}>
              <T _str={title} />
            </ButtonText>
          </Box>
        </HStack>
      </Box>
    );
  };

  return (
    <Button
      className={`${buttonStyle({
        variant,
        size: "md",
        disabled,
      })} ${className} ${iconPosition === "after" ? "flex-row-reverse" : ""}`}
      onPress={handlePressButton}
      disabled={disabled}
      data-test-id={testID}
    >
      {renderButtonContent()}
    </Button>
  );
};

export default AppButton;
